'use client';

import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CheckCircle2, Circle, Eye, EyeOff } from 'lucide-react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import type { SignupFormData } from './signup-wizard';

interface StepAccountProps {
  form: UseFormReturn<SignupFormData>;
  isLoading: boolean;
}

const PASSWORD_RULES = [
  { label: '12+ characters', test: (v: string) => v.length >= 12 },
  { label: 'Uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'Lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { label: 'Number', test: (v: string) => /[0-9]/.test(v) },
  { label: 'Special character', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
];

// Tells browser password generators what we require
// Supported by Safari and Chrome's "Suggest Strong Password"
const PASSWORD_RULES_ATTR =
  'minlength: 12; required: upper; required: lower; required: digit; required: special;';

function PasswordRequirements({ password }: { password: string }) {
  const hasStartedTyping = password.length > 0;

  if (!hasStartedTyping) {
    return (
      <p className="text-sm text-gray-500">
        At least 12 characters with uppercase, lowercase, number, and special
        character
      </p>
    );
  }

  return (
    <ul className="space-y-1 mt-1">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(password);
        return (
          <li key={rule.label} className="flex items-center gap-1.5">
            {met ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
            ) : (
              <Circle className="h-3.5 w-3.5 text-red-400 shrink-0" />
            )}
            <span
              className={`text-xs ${met ? 'text-green-600' : 'text-red-500'}`}
            >
              {rule.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function StepAccount({ form, isLoading }: StepAccountProps) {
  const password = form.watch('adminPassword') ?? '';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Your Account</h2>
        <p className="text-sm text-gray-500">Create your admin login</p>
      </div>

      <FormField
        control={form.control}
        name="adminName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Your Name (Optional)</FormLabel>
            <FormControl>
              <Input
                placeholder="John Smith"
                {...field}
                disabled={isLoading}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="adminEmail"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input
                type="email"
                placeholder="admin@yourcharity.org"
                autoComplete="off"
                {...field}
                disabled={isLoading}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="adminPassword"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Password</FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  autoComplete="new-password"
                  // @ts-expect-error — passwordrules is a valid HTML attribute but not in React's types
                  passwordrules={PASSWORD_RULES_ATTR}
                  minLength={12}
                  {...field}
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </FormControl>
            <PasswordRequirements password={password} />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="confirmPassword"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Confirm Password</FormLabel>
            <FormControl>
              <div className="relative">
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="••••••••••••"
                  autoComplete="new-password"
                  {...field}
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowConfirm((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
